// src/components/PdfViewer.jsx
import { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

// Use local worker from public folder
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.js`;

export default function PdfViewer({ url }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfError, setPdfError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Detect mobile devices
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPdfError(null);
  }

  function onDocumentError(error) {
    setPdfError(error?.message || 'Unknown error');
    // Also log to console for debugging
    // eslint-disable-next-line no-console
    console.error('PDF load error:', error);
  }

  // On mobile, show a card instead of embedding
  if (isMobile) {
    return (
      <div className="w-full">
        {/* Mobile PDF Card */}
        <div className="border-2 border-slate-900 dark:border-slate-600 bg-white dark:bg-slate-800 p-8 text-center transition-colors duration-300">
          <div className="mb-4 text-6xl">📄</div>
          <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">Resume PDF</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
            Tap below to view or download the resume
          </p>

          <div className="flex flex-col gap-3">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full px-6 py-3 bg-slate-900 dark:bg-slate-700 text-white text-base font-medium hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors"
            >
              Open Resume
            </a>
            <a
              href={url}
              download
              className="block w-full px-6 py-3 border-2 border-slate-900 dark:border-slate-600 text-slate-900 dark:text-slate-100 text-base font-medium hover:bg-slate-900 dark:hover:bg-slate-600 hover:text-white transition-colors"
            >
              Download PDF
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Desktop: Side thumbnails + main page view
  return (
    <div className="flex w-full" style={{ minHeight: '80vh', maxHeight: '90vh' }}>
      {/* Sidebar Thumbnails */}
      <div className="overflow-y-auto border-r border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-2" style={{ width: 180, maxHeight: '90vh' }}>
        <div className="font-semibold text-slate-700 dark:text-slate-200 mb-2 pl-2">Pages</div>
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentError}
          loading={<div className='text-center'>Loading...</div>}
        >
          {numPages && Array.from(new Array(numPages), (el, idx) => (
            <div
              key={idx}
              className={`mb-4 cursor-pointer rounded-lg border-2 ${pageNumber === idx + 1 ? 'border-blue-500' : 'border-transparent'} hover:border-blue-400 transition-all`}
              onClick={() => setPageNumber(idx + 1)}
            >
              <Page
                pageNumber={idx + 1}
                width={140}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                className="rounded"
              />
              <div className="text-center text-xs text-slate-500 py-1">{idx + 1}</div>
            </div>
          ))}
        </Document>
      </div>
      {/* Main Page Viewer */}
      <div className="flex-1 flex flex-col items-center justify-center overflow-y-auto bg-white dark:bg-slate-800" style={{ maxHeight: '90vh' }}>
        {pdfError ? (
          <div className="text-center text-red-500 p-4">Failed to load PDF: {pdfError}</div>
        ) : (
          <Document
            file={url}
            loading={<div className='text-center'>Loading...</div>}
          >
            <Page
              pageNumber={pageNumber}
              width={800}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              className="shadow-lg rounded-lg my-8"
            />
          </Document>
        )}
        {/* Page navigation */}
        {numPages && (
          <div className="flex items-center justify-center gap-4 my-4">
            <button
              onClick={() => setPageNumber(p => Math.max(1, p - 1))}
              disabled={pageNumber === 1}
              className="px-3 py-1 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-slate-700 dark:text-slate-200">Page {pageNumber} of {numPages}</span>
            <button
              onClick={() => setPageNumber(p => Math.min(numPages, p + 1))}
              disabled={pageNumber === numPages}
              className="px-3 py-1 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
